import type { EntityStatus } from '@monorepo/shared/schemas/common'
import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useCreateAgent, useUpdateAgent } from '../hooks/useAgents'
import type { Agent, UpdateAgent } from '../types/agent.types'

interface AgentDrawerProps {
  agent?: Agent
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AgentDrawer({ agent, open, onOpenChange }: AgentDrawerProps) {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<UpdateAgent>({
    name: '',
    description: '',
    external_id: '',
    status: ENTITY_STATUS.active,
  })

  const updateAgent = useUpdateAgent()
  const createAgent = useCreateAgent()

  // Update form data when agent changes or reset when creating
  useEffect(() => {
    if (agent) {
      // Edit mode: populate with agent data
      setFormData({
        name: agent.name,
        description: agent.description || '',
        external_id: agent.external_id || '',
        status: agent.status,
      })
    } else {
      // Create mode: reset to empty form
      setFormData({
        name: '',
        description: '',
        external_id: '',
        status: ENTITY_STATUS.draft,
      })
    }
  }, [agent])

  const handleSave = async () => {
    try {
      if (agent) {
        // Edit mode: update existing agent
        await updateAgent.mutateAsync({
          id: agent.id,
          data: formData,
        })
        onOpenChange(false)
      } else {
        // Create mode: create new agent
        const newAgent = await createAgent.mutateAsync({
          name: formData.name!,
          description: formData.description || undefined,
          external_id: formData.external_id || undefined,
          status: formData.status || ENTITY_STATUS.draft,
        })
        // Close drawer and navigate to agent detail page
        onOpenChange(false)
        navigate({ to: '/agents/$agentId', params: { agentId: newAgent.id } })
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleCancel = () => {
    // Reset form data to original values
    if (agent) {
      setFormData({
        name: agent.name,
        description: agent.description || '',
        external_id: agent.external_id || '',
        status: agent.status,
      })
    }
    onOpenChange(false)
  }

  const hasChanges =
    agent &&
    (formData.name !== agent.name ||
      formData.description !== (agent.description || '') ||
      formData.external_id !== (agent.external_id || '') ||
      formData.status !== agent.status)

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {agent ? 'Edit Agent Details' : 'Create New Agent'}
          </DrawerTitle>
          <DrawerDescription>
            {agent
              ? 'Update the agent information and settings.'
              : 'Configure your new agent to start tracking activities.'}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4'>
          <div className='space-y-4'>
            {/* Agent Name */}
            <div>
              <Label htmlFor='agent-name'>Agent Name *</Label>
              <Input
                id='agent-name'
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder='Enter agent name'
                className='mt-1'
              />
            </div>

            {/* External ID */}
            <div>
              <Label htmlFor='external-id'>External ID</Label>
              <Input
                id='external-id'
                value={formData.external_id || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    external_id: e.target.value,
                  }))
                }
                placeholder='Optional unique identifier'
                className='mt-1'
              />
              <p className='text-xs text-slate-500 mt-1'>
                Use this to reference the agent from your system
              </p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder='Describe what this agent does'
                rows={4}
                className='mt-1'
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor='status'>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value: EntityStatus) =>
                  setFormData(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
                  <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-500 mt-1'>
                Active agents can process actions, draft agents are disabled
              </p>
            </div>

            {/* Agent Info */}
            {agent && (
              <div className='pt-4 border-t'>
                <h4 className='font-medium text-sm text-slate-700 dark:text-slate-300 mb-3'>
                  Agent Information
                </h4>
                <div className='space-y-2 text-xs text-slate-500'>
                  <div>
                    <span className='font-medium'>ID:</span> {agent.id}
                  </div>
                  <div>
                    <span className='font-medium'>Created:</span>{' '}
                    {new Date(agent.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Updated:</span>{' '}
                    {new Date(agent.updated_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Total Actions:</span>{' '}
                    {agent.totalSignalsCount || 0}
                  </div>
                  <div>
                    <span className='font-medium'>Billable Signals:</span>{' '}
                    {agent.billableSignalsCount || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                disabled={updateAgent.isPending || createAgent.isPending}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleSave}
              disabled={
                updateAgent.isPending ||
                createAgent.isPending ||
                (agent && !hasChanges) ||
                !formData.name?.trim()
              }
              className='flex-1'
            >
              {(updateAgent.isPending || createAgent.isPending) && (
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
              )}
              <Save className='w-4 h-4 mr-2' />
              {agent ? 'Save Changes' : 'Create Agent'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
